import React, {
  useState,
  useCallback,
  ChangeEvent,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import Layout from '@/components/Layout/Layout';
import MainHeading from '@/components/common/MainHeading';
import TabContainer from '@/components/common/TabContainer';
import CommonTable, { ITableColumn } from '@/components/common/CommonTable';
import FilterComponent from '@/components/common/FilterComponent';
import PlusIcon from '@/components/Icons/PlusIcon';
import ViewIcon from '@/components/Icons/ViewIcon';
import EditIcon from '@/components/Icons/EditIcon';

import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import {
  showToastNotification,
  getFilterUrl,
  orderByList,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { RootState } from '@/redux/store';
import { setUpdateFilterStates } from '@/redux/features/UserSlice';
import { IFilterSideBarForm } from '@/interfaces/common.model';
import {
  IOffboardingListResponse,
  IPageStates,
  ITableData,
} from '@/interfaces/offbording.model';

import { IAttendanceStatus } from '@/interfaces/attendance.model';

const getStatusBadge = (status: string) => {
  const map: Record<string, { color: string; bg: string }> = {
    Initiated: { color: '#0056b3', bg: '#cce5ff' },
    'In Progress': { color: '#856404', bg: '#fff3cd' },
    Completed: { color: '#155724', bg: '#d4edda' },
  };
  const style = map[status] || { color: '#6c757d', bg: '#e9ecef' };
  return (
    <span
      style={{
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: 500,
        color: style.color,
        backgroundColor: style.bg,
        display: 'inline-block',
      }}
    >
      {status}
    </span>
  );
};

const Offboarding: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const timeoutId = useRef<NodeJS.Timeout | null>(null);

  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );
  const tableView = useSelector((state: RootState) => state?.user?.tableView);

  const [pageStates, setPageStates] = useState<IPageStates>({
    tableLoader: false,
    offboardingList: [],
    searchQuery: '',
  });

  const statusOptions: IAttendanceStatus[] = [
    { value: 'Initiated', label: 'Initiated' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Completed', label: 'Completed' },
  ];

  const tableHeading = useMemo(() => {
    const fullColumns: ITableColumn<ITableData>[] = [
      { value: 'index', label: '#' },
      { value: 'empNameWithImage', label: 'Emp. Name' },
      { value: 'employeeCode', label: 'Emp. Code' },
      { value: 'email', label: 'Email' },
      { value: 'phone', label: 'Phone' },
      { value: 'joiningDate', label: 'Joining Date' },
      { value: 'lastWorkingDay', label: 'Last Working Day' },
      { value: 'tenure', label: 'Tenure' },
      { value: 'assetHandover', label: 'Asset Handover' },
      { value: 'isCompletedProject', label: 'Project Completed' },
      { value: 'handoverPerson', label: 'Handover Person' },
      { value: 'statusBadge', label: 'Status' },
    ];

    if (tableView === 'grid') {
      return fullColumns
        .filter(
          (col) =>
            ![
              'tenure',
              'assetHandover',
              'isCompletedProject',
              'handoverPerson',
            ].includes(col.value as string),
        )
        .map((col) => {
          if (col.value === 'statusBadge') {
            return {
              value: 'statusLabel',
              label: 'Status',
            } as ITableColumn<ITableData>;
          }
          return col;
        });
    }
    return fullColumns;
  }, [tableView]);

  const handleView = (data: ITableData) => {
    navigate(RoutesLink?.Offborading + `/${data?.id}`);
  };
  const handleEdit = (data: ITableData) => {
    navigate(RoutesLink?.updateOffBording + `/${data?.id}`);
  };
  const ActionData = [
    {
      label: 'View',
      className: 'btn btn-link',
      icon: <ViewIcon />,
      title: 'view',
      clickHandler: handleView,
    },
    {
      label: 'Edit',
      className: 'btn btn-link',
      icon: <EditIcon />,
      title: 'edit',
      clickHandler: handleEdit,
    },
  ];

  const getOffboardingList = useCallback(async () => {
    const status = filterDetails?.empStatus;
    const validStatus =
      status && ['Initiated', 'In Progress', 'Completed'].includes(status)
        ? status
        : undefined;

    const filter = {
      pageNumber: filterDetails?.currentPage || 1,
      pageSize: filterDetails?.pageSize || 10,
      search: pageStates.searchQuery,
      orderBy: filterDetails?.orderBy,
      status: validStatus,
    };

    Object.keys(filter).forEach(
      (key) =>
        filter[key as keyof typeof filter] === undefined &&
        delete filter[key as keyof typeof filter],
    );

    try {
      setPageStates((prev) => ({ ...prev, tableLoader: true }));
      const API =
        API_URL + API_ENDPOINT.getOffboardingList + '?' + getFilterUrl(filter);

      const res: IOffboardingListResponse = await callApi(API, {
        method: 'GET',
      });

      if (res?.status === true) {
        const list = res.data.offBoard || [];
        const mappedData: ITableData[] = list.map((item, index) => ({
          id: item.id,
          index: String(index + 1),
          empNameWithImage:
            item.employeeUser?.name +
            '##' +
            (item.employeeUser?.profilePhoto || ''),
          employeeCode: item.employeeUser?.employeeCode ?? '-',
          email: item.employeeUser?.email ?? '-',
          phone: item.employeeUser?.phone ?? '-',
          joiningDate: item.joiningDate
            ? moment(item.joiningDate).format('DD-MMM-YYYY')
            : '--',
          lastWorkingDay: item.lastWorkingDay
            ? moment(item.lastWorkingDay).format('DD-MMM-YYYY')
            : '--',
          tenure: String(item.tenure ?? '--'),
          assetHandover: item.assetHandover ? 'Yes' : 'No',
          isCompletedProject: item.isCompletedProject ? 'Yes' : 'No',
          handoverPerson: item.handoverUser?.name ?? '--',
          status: item.status ?? '--',
          statusLabel: item.status ?? '--',
          statusBadge: getStatusBadge(item.status ?? '--'),
        }));

        setPageStates((prev) => ({ ...prev, offboardingList: mappedData }));

        dispatch(
          setUpdateFilterStates({
            count: res.data.pageInfo?.total,
            totalPages: res.data.pageInfo?.totalPages,
          }),
        );
      } else {
        showToastNotification('danger', res?.message || 'Failed to fetch data');
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((prev) => ({ ...prev, tableLoader: false }));
    }
  }, [
    filterDetails?.currentPage,
    filterDetails?.pageSize,
    filterDetails?.orderBy,
    filterDetails?.empStatus,
    pageStates.searchQuery,
    dispatch,
  ]);

  const sortHandler = (data: Partial<IFilterSideBarForm> | null) => {
    // Clear all filters
    if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
      dispatch(
        setUpdateFilterStates({
          empStatus: undefined,
          currentPage: 1,
        }),
      );
      return;
    }

    let status = data?.attendanceStatus || data?.status;
    if (status === 'all' || status === '' || status === undefined) {
      status = undefined;
    }

    dispatch(
      setUpdateFilterStates({
        empStatus: status,
        currentPage: 1,
      }),
    );
  };

  const handleChangePageSize = (value: number) => {
    dispatch(
      setUpdateFilterStates({
        currentPage: 1,
        pageSize: value,
      }),
    );
  };
  const pageChangeHandler = (pageNo: number) => {
    if (pageNo > 0 && pageNo <= (filterDetails?.totalPages || 1)) {
      dispatch(
        setUpdateFilterStates({
          currentPage: pageNo,
        }),
      );
    }
  };
  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    timeoutId.current = setTimeout(() => {
      dispatch(setUpdateFilterStates({ currentPage: 1 }));
      setPageStates((prev) => ({ ...prev, searchQuery: value }));
    }, 500);
  };
  const orderByHandler = (e: ChangeEvent<HTMLSelectElement>) => {
    dispatch(
      setUpdateFilterStates({
        orderBy: e.target.value,
        currentPage: 1,
      }),
    );
  };

  useEffect(() => {
    if (
      filterDetails?.empStatus &&
      !['Initiated', 'In Progress', 'Completed'].includes(
        filterDetails.empStatus,
      )
    ) {
      dispatch(setUpdateFilterStates({ empStatus: undefined, currentPage: 1 }));
    }
  }, []);

  useEffect(() => {
    getOffboardingList();
  }, [getOffboardingList]);

  return (
    <>
      <Layout>
        <div className="content">
          <MainHeading
            title="Offboarding"
            buttonText="Add Offboarding"
            buttonIcon={<PlusIcon />}
            buttonLink={RoutesLink?.createOffBording}
          />
          <TabContainer>
            <FilterComponent
              filterArray={{ isShow: true }}
              attendanceList={statusOptions}
              userStatus={false}
              sortHandler={sortHandler}
              handleSearch={handleSearchChange}
              orderByHandler={orderByHandler}
              orderByList={orderByList}
              isGrid
            />
            <CommonTable<ITableData>
              handleViewDetail={handleView}
              tableHeading={tableHeading}
              tableData={pageStates.offboardingList}
              ActionData={ActionData}
              currentPage={filterDetails?.currentPage || 1}
              pageChangeHandler={pageChangeHandler}
              isLoading={pageStates.tableLoader}
              totalPages={filterDetails?.totalPages || 0}
              count={filterDetails?.count || 0}
              handleChangePageSize={handleChangePageSize}
              pageSize={filterDetails?.pageSize || 10}
            />
          </TabContainer>
        </div>
      </Layout>
    </>
  );
};

export default Offboarding;
